import { useCallback } from 'react'
import { ApolloError } from '@apollo/client'
import { IError, Maybe } from 'types/graphqlTypes'
import { useCreateCarrierOptionMutation } from '../graphql/CreateCarrierOptionMutation'
import { useUpdateCarrierOptionMutation } from '../graphql/UpdateCarrierOptionMutation'
import { TCarrierOptionSchema } from '../schema/CarrierOptionSchema'
import { getCreateBidInput, getUpdateBidInput } from '../utils'

interface ICarrierOptionMutationResult {
  carrierOptionId: string | undefined
  errors: IError[] | undefined
}

export interface IUseCarrierOptionMutationsState {
  createCarrierOption: (
    carrierOption: TCarrierOptionSchema
  ) => Promise<ICarrierOptionMutationResult>
  updateCarrierOption: (
    carrierOption: TCarrierOptionSchema
  ) => Promise<ICarrierOptionMutationResult>
  loading: boolean
  errors: Maybe<ApolloError>
}

export interface IUseCarrierOptionMutationsProps {
  shipmentId: string
  carrierOptionId: Maybe<string>
}

export const useCarrierOptionMutations = ({
  carrierOptionId,
  shipmentId,
}: IUseCarrierOptionMutationsProps): IUseCarrierOptionMutationsState => {
  const [
    createCarrierOptionMutation,
    { error: createErrors, loading: createLoading },
  ] = useCreateCarrierOptionMutation()
  const [
    updateCarrierOptionMutation,
    { error: updateErrors, loading: updateLoading },
  ] = useUpdateCarrierOptionMutation()

  const createCarrierOption = useCallback(
    async (formData: TCarrierOptionSchema) => {
      const { data } = await createCarrierOptionMutation({
        variables: {
          input: getCreateBidInput({
            formData,
            shipmentId,
          }),
        },
      })
      const { createBid } = data || {}
      const { bid, errors } = createBid || {}
      const { id: carrierOptionId } = bid || {}
      return { carrierOptionId, errors }
    },
    [createCarrierOptionMutation, shipmentId]
  )

  const updateCarrierOption = useCallback(
    async (formData: TCarrierOptionSchema) => {
      const { data } = await updateCarrierOptionMutation({
        variables: {
          input: getUpdateBidInput({
            carrierOptionId: carrierOptionId || '',
            formData,
            shipmentId,
          }),
        },
      })
      const { updateBid } = data || {}
      const { bid, errors } = updateBid || {}
      const { id: optionId } = bid || {}
      return { carrierOptionId: optionId, errors }
    },
    [updateCarrierOptionMutation, carrierOptionId, shipmentId]
  )

  return {
    createCarrierOption,
    updateCarrierOption,
    loading: createLoading || updateLoading,
    errors: createErrors || updateErrors,
  }
}
